import { format as dateFnsFormat } from "date-fns";

/**
 * Parse a date string as a local date, avoiding timezone conversion issues.
 *
 * When a date string like "1971-10-27" or "1971-10-27T00:00:00" is parsed with
 * new Date(), JavaScript treats it as UTC, which can shift the date by one day
 * when displayed in local timezone. This function ensures the date is parsed
 * as a local date.
 *
 * @param dateString - Date string from database (ISO format or date-only)
 * @returns Date object in local timezone
 */
export function parseLocalDate(
  dateString: string | Date | null | undefined,
): Date | null {
  if (!dateString) return null;

  if (dateString instanceof Date) return dateString;

  // Extract just the date part (YYYY-MM-DD) from ISO string or date-only string
  const dateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (dateMatch) {
    const [, year, month, day] = dateMatch;
    // Month is 0-indexed in JavaScript Date
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }

  // Fallback to normal parsing if pattern doesn't match
  return new Date(dateString);
}

/**
 * Format a date string as MM/dd/yyyy in local timezone
 *
 * @param dateString - Date string from database
 * @returns Formatted date string or empty string if null/invalid
 */
export function formatLocalDate(
  dateString: string | Date | null | undefined,
): string {
  const date = parseLocalDate(dateString);
  if (!date || isNaN(date.getTime())) return "";

  return dateFnsFormat(date, "MM/dd/yyyy");
}

/**
 * Convert a date to locale date string (e.g., "10/27/1971")
 *
 * @param dateString - Date string from database
 * @returns Locale date string or empty string if null/invalid
 */
export function toLocaleDateString(
  dateString: string | Date | null | undefined,
): string {
  const date = parseLocalDate(dateString);
  if (!date || isNaN(date.getTime())) return "";

  return date.toLocaleDateString();
}
